import { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import {
    OrbitCatalogEnum,
    OrbitPreferredContactMethodEnum,
} from '../../../../shared/types/orbit';
import { Attachment, ISelect, ParamEnum } from '../../../../shared/types/shared';
import { fileToBase64 } from '../../../../shared/utils/files';
import { asyncGetParams } from '../../../../services/param.service';
import { useOrbitCatalog } from '../hooks/useOrbitCatalog';
import { initialValues, validationSchema } from './formData';
import { OrbitPqrInfoProps } from '../sectionProps';

const ORBIT_PREFERRED_CONTACT_METHOD_TO_ORBIT: Record<
    string,
    OrbitPreferredContactMethodEnum
> = {
    [`${ParamEnum.ORBIT_PREFERRED_CONTACT_METHOD}_EMAIL`]:
        OrbitPreferredContactMethodEnum.Email,
    [`${ParamEnum.ORBIT_PREFERRED_CONTACT_METHOD}_PHYSICAL_MAIL`]:
        OrbitPreferredContactMethodEnum.Mail,
};

const COLOMBIA_COUNTRY_CODE = '170';
const COLOMBIA_ISO_CODE = 'CO';

const isColombiaCountry = (code?: string, name?: string) => {
    const normalizedCode = String(code ?? '').trim().toUpperCase();

    return (
        normalizedCode === COLOMBIA_ISO_CODE ||
        normalizedCode === COLOMBIA_COUNTRY_CODE ||
        name?.trim().toLowerCase() === 'colombia'
    );
};

export const useOrbitPqrInfo = ({
    onChangeFormik,
    readOnly,
    seedValues,
}: OrbitPqrInfoProps) => {
    const {
        items: countries,
        loading: loadingCountries,
        error: countriesError,
    } = useOrbitCatalog(OrbitCatalogEnum.Countries);

    const {
        items: caseTypes,
        loading: loadingCaseTypes,
        error: caseTypesError,
    } = useOrbitCatalog(OrbitCatalogEnum.CaseTypes);

    const {
        items: departments,
        loading: loadingDepartments,
        error: departmentsError,
    } = useOrbitCatalog(OrbitCatalogEnum.Departments);

    const defaultCountry = useMemo(
        () =>
            countries.find((country) =>
                isColombiaCountry(
                    String(country.code ?? country.id),
                    country.name
                )
            ),
        [countries]
    );

    const colombiaCountryId = String(defaultCountry?.id ?? '');

    const [preferredContactMethods, setPreferredContactMethods] = useState<
        ISelect[]
    >([]);

    const formik = useFormik({
        initialValues: {
            ...initialValues,
            ...(readOnly && seedValues ? seedValues : {}),
            country: readOnly ? seedValues?.country ?? '' : colombiaCountryId,
        },
        validationSchema: validationSchema(colombiaCountryId),
        enableReinitialize: readOnly || Boolean(defaultCountry?.id),
        onSubmit: () => undefined,
    });

    useEffect(() => {
        asyncGetParams(ParamEnum.ORBIT_PREFERRED_CONTACT_METHOD).then((items) => {
            const options = (items || []).reduce<ISelect[]>((acc, item) => {
                const orbitValue =
                    ORBIT_PREFERRED_CONTACT_METHOD_TO_ORBIT[item.code || ''];

                if (!orbitValue) return acc;

                acc.push({
                    id: orbitValue,
                    name: item.name,
                    code: item.code,
                });
                return acc;
            }, []);

            setPreferredContactMethods(options);

            if (readOnly) return;

            const defaultEmail = options.find(
                (item) => item.id === OrbitPreferredContactMethodEnum.Email
            );

            if (
                defaultEmail &&
                !formik.values.preferredContactMethod
            ) {
                formik.setFieldValue(
                    'preferredContactMethod',
                    defaultEmail.id
                );
            }
        });
    }, [readOnly]);

    const {
        items: cities,
        loading: loadingCities,
        error: citiesError,
    } = useOrbitCatalog(
        OrbitCatalogEnum.Cities,
        { department_code: String(formik.values.department) },
        Boolean(formik.values.department)
    );

    const isColombia = useMemo(() => {
        if (readOnly) {
            return (
                Boolean(formik.values.department || formik.values.city) ||
                /colombia/i.test(String(formik.values.country))
            );
        }

        const selectedCountry = countries.find(
            (country) => String(country.id) === String(formik.values.country)
        );

        return isColombiaCountry(
            String(selectedCountry?.code ?? selectedCountry?.id),
            selectedCountry?.name
        );
    }, [
        countries,
        formik.values.city,
        formik.values.country,
        formik.values.department,
        readOnly,
    ]);

    useEffect(() => {
        if (onChangeFormik) onChangeFormik(formik);
    }, [formik.values, formik.errors, formik.isValid]);

    useEffect(() => {
        if (readOnly || !isColombia) {
            if (readOnly) return;
            formik.setFieldValue('department', '');
            formik.setFieldValue('city', '');
        }
    }, [isColombia, readOnly]);

    useEffect(() => {
        if (readOnly || !formik.touched.department) return;
        formik.setFieldValue('city', '');
    }, [formik.values.department, readOnly, formik.touched.department]);

    const handleAttachmentsChange = async (files: File[]) => {
        const next: Attachment[] = [];

        for await (const file of files) {
            const encoded = await fileToBase64(file);
            if (encoded) next.push(encoded);
        }

        formik.setFieldValue('attachments', next);
    };

    return {
        formik,
        preferredContactMethods,
        caseTypes,
        loadingCaseTypes,
        caseTypesError,
        countries,
        loadingCountries,
        countriesError:
            countriesError ||
            (!loadingCountries && !countries.length
                ? 'No se pudieron cargar los países de Orbit.'
                : undefined),
        departments,
        cities,
        loadingDepartments,
        loadingCities,
        departmentsError,
        citiesError,
        isColombia,
        handleAttachmentsChange,
    };
};
